/**
 * CallSounds - Premium Audio Service for Call Notifications
 * 
 * Uses Web Audio API to generate realistic, pleasant calling sounds:
 * - Incoming call: Modern phone ring with harmonics
 * - Outbound ringing: Standard ITU-T ringback tone
 * - Connected: Soft pleasant chime
 * - Ended: Gentle hang-up beep
 */

class CallSoundsService {
    constructor() {
        this.audioContext = null;
        this.currentSound = null;
        this.oscillators = [];
        this.isPlaying = false;
        this.soundType = null;
        this.masterGain = null;
    }

    /**
     * Initialize AudioContext (must be called after user interaction)
     */
    init() {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            // Create master gain for overall volume control
            this.masterGain = this.audioContext.createGain();
            this.masterGain.connect(this.audioContext.destination);
            this.masterGain.gain.value = 0.5; // 50% master volume
        }
        // Resume if suspended
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }
        return this;
    }

    /**
     * Create a rich tone with harmonics for more natural sound
     */
    createRichTone(baseFreq, duration, volume = 0.2) {
        if (!this.audioContext) this.init();

        const gainNode = this.audioContext.createGain();
        gainNode.connect(this.masterGain);
        gainNode.gain.value = 0;

        // Base tone
        const osc1 = this.audioContext.createOscillator();
        osc1.frequency.value = baseFreq;
        osc1.type = 'sine';

        // Slight harmonic for richness
        const osc2 = this.audioContext.createOscillator();
        osc2.frequency.value = baseFreq * 2; // Octave above
        osc2.type = 'sine';

        // Create gain nodes for mixing
        const gain1 = this.audioContext.createGain();
        const gain2 = this.audioContext.createGain();
        gain1.gain.value = volume;
        gain2.gain.value = volume * 0.15; // Softer harmonic

        osc1.connect(gain1);
        osc2.connect(gain2);
        gain1.connect(gainNode);
        gain2.connect(gainNode);

        return { oscillators: [osc1, osc2], gainNode, gains: [gain1, gain2] };
    }

    /**
     * Play incoming call sound - Modern smartphone ring
     * Pattern: Pleasant two-tone melody, pause, repeat
     * Sounds like a professional phone ringtone
     */
    playIncomingCall() {
        if (this.isPlaying && this.soundType === 'incoming') return;

        this.stop();
        this.init();
        this.isPlaying = true;
        this.soundType = 'incoming';

        const playRingPattern = () => {
            if (!this.isPlaying || this.soundType !== 'incoming') return;

            const time = this.audioContext.currentTime;

            // Modern smartphone-style ring: E5-G5 pattern
            // First ring burst (3 short beeps)
            const notes = [659.25, 783.99, 659.25]; // E5, G5, E5
            const beepDuration = 0.08;
            const beepGap = 0.06;

            notes.forEach((freq, i) => {
                const startTime = time + i * (beepDuration + beepGap);
                const tone = this.createRichTone(freq, beepDuration, 0.18);

                tone.oscillators.forEach(osc => osc.start(startTime));

                // Smooth envelope
                tone.gainNode.gain.setValueAtTime(0, startTime);
                tone.gainNode.gain.linearRampToValueAtTime(1, startTime + 0.01);
                tone.gainNode.gain.setValueAtTime(1, startTime + beepDuration - 0.02);
                tone.gainNode.gain.linearRampToValueAtTime(0, startTime + beepDuration);

                tone.oscillators.forEach(osc => osc.stop(startTime + beepDuration + 0.01));
            });

            // Second ring burst (longer, descending)
            const burstStart = time + 0.5;
            const longNotes = [783.99, 659.25]; // G5, E5

            longNotes.forEach((freq, i) => {
                const startTime = burstStart + i * 0.15;
                const duration = 0.12;
                const tone = this.createRichTone(freq, duration, 0.18);

                tone.oscillators.forEach(osc => osc.start(startTime));

                tone.gainNode.gain.setValueAtTime(0, startTime);
                tone.gainNode.gain.linearRampToValueAtTime(1, startTime + 0.015);
                tone.gainNode.gain.setValueAtTime(1, startTime + duration - 0.03);
                tone.gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

                tone.oscillators.forEach(osc => osc.stop(startTime + duration + 0.01));
            });

            // Repeat pattern every 2.5 seconds
            this.currentSound = setTimeout(() => playRingPattern(), 2500);
        };

        playRingPattern();
    }

    /**
     * Play outbound ringing sound - Standard ringback tone
     * ITU-T recommended: 400-450Hz dual tone, 1s on, 4s off (simplified to 2s)
     * Feels like you're waiting for the other party to answer
     */
    playOutboundRinging() {
        if (this.isPlaying && this.soundType === 'ringing') return;

        this.stop();
        this.init();
        this.isPlaying = true;
        this.soundType = 'ringing';

        const playRingback = () => {
            if (!this.isPlaying || this.soundType !== 'ringing') return;

            const time = this.audioContext.currentTime;
            const duration = 1.0; // 1 second tone

            // Create standard ringback dual-tone
            const gainNode = this.audioContext.createGain();
            gainNode.connect(this.masterGain);

            // US/Indian ringback: 440Hz + 480Hz (classic phone ringback)
            const osc1 = this.audioContext.createOscillator();
            const osc2 = this.audioContext.createOscillator();

            osc1.frequency.value = 440;
            osc2.frequency.value = 480;
            osc1.type = 'sine';
            osc2.type = 'sine';

            // Individual gains for mixing
            const gain1 = this.audioContext.createGain();
            const gain2 = this.audioContext.createGain();
            gain1.gain.value = 0.12;
            gain2.gain.value = 0.12;

            osc1.connect(gain1);
            osc2.connect(gain2);
            gain1.connect(gainNode);
            gain2.connect(gainNode);

            osc1.start(time);
            osc2.start(time);

            // Smooth envelope - fade in and out
            gainNode.gain.setValueAtTime(0, time);
            gainNode.gain.linearRampToValueAtTime(1, time + 0.05);
            gainNode.gain.setValueAtTime(1, time + duration - 0.1);
            gainNode.gain.linearRampToValueAtTime(0, time + duration);

            osc1.stop(time + duration + 0.05);
            osc2.stop(time + duration + 0.05);

            // Pattern: 1s on, 3s off (total 4s cycle - standard ringback pattern)
            this.currentSound = setTimeout(() => playRingback(), 4000);
        };

        playRingback();
    }

    /**
     * Play call connected sound - Pleasant confirmation chime
     * Two ascending tones that feel like "connected!"
     */
    playConnected() {
        this.stop();
        this.init();

        const time = this.audioContext.currentTime;

        // Soft, pleasant two-note chime (think iPhone connect sound)
        const notes = [523.25, 659.25]; // C5, E5 - major third, pleasant

        notes.forEach((freq, i) => {
            const startTime = time + i * 0.12;
            const duration = 0.2;

            const gainNode = this.audioContext.createGain();
            gainNode.connect(this.masterGain);

            const osc = this.audioContext.createOscillator();
            osc.frequency.value = freq;
            osc.type = 'sine';
            osc.connect(gainNode);

            osc.start(startTime);

            // Bell-like envelope
            gainNode.gain.setValueAtTime(0, startTime);
            gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

            osc.stop(startTime + duration + 0.05);
        });
    }

    /**
     * Play call ended sound - Soft disconnect beep
     * Single short beep, like a phone hang-up
     */
    playEnded() {
        this.stop();
        this.init();

        const time = this.audioContext.currentTime;

        // Classic phone disconnect: short 480Hz beep
        const gainNode = this.audioContext.createGain();
        gainNode.connect(this.masterGain);

        const osc = this.audioContext.createOscillator();
        osc.frequency.value = 480;
        osc.type = 'sine';
        osc.connect(gainNode);

        osc.start(time);

        // Quick beep with gentle fade
        const duration = 0.15;
        gainNode.gain.setValueAtTime(0, time);
        gainNode.gain.linearRampToValueAtTime(0.2, time + 0.01);
        gainNode.gain.setValueAtTime(0.2, time + duration - 0.05);
        gainNode.gain.linearRampToValueAtTime(0, time + duration);

        osc.stop(time + duration + 0.02);
    }

    /**
     * Play dial tone - for when dialing starts
     * Standard 350Hz + 440Hz US dial tone
     */
    playDialing() {
        if (this.isPlaying && this.soundType === 'dialing') return;

        this.stop();
        this.init();
        this.isPlaying = true;
        this.soundType = 'dialing';

        const time = this.audioContext.currentTime;

        const gainNode = this.audioContext.createGain();
        gainNode.connect(this.masterGain);
        gainNode.gain.value = 0.15;

        // US dial tone: 350Hz + 440Hz
        const osc1 = this.audioContext.createOscillator();
        const osc2 = this.audioContext.createOscillator();

        osc1.frequency.value = 350;
        osc2.frequency.value = 440;
        osc1.type = 'sine';
        osc2.type = 'sine';

        const gain1 = this.audioContext.createGain();
        const gain2 = this.audioContext.createGain();
        gain1.gain.value = 0.5;
        gain2.gain.value = 0.5;

        osc1.connect(gain1);
        osc2.connect(gain2);
        gain1.connect(gainNode);
        gain2.connect(gainNode);

        osc1.start(time);
        osc2.start(time);

        this.oscillators = [osc1, osc2];

        // Auto-stop after 3 seconds if not manually stopped
        this.currentSound = setTimeout(() => {
            this.stop();
        }, 3000);
    }

    /**
     * Play error/busy tone
     * Standard busy signal: 480Hz + 620Hz, pulsing
     */
    playBusy() {
        this.stop();
        this.init();
        this.isPlaying = true;
        this.soundType = 'busy';

        let pulseCount = 0;
        const maxPulses = 4;

        const playPulse = () => {
            if (!this.isPlaying || this.soundType !== 'busy' || pulseCount >= maxPulses) {
                this.stop();
                return;
            }

            const time = this.audioContext.currentTime;
            const duration = 0.25;

            const gainNode = this.audioContext.createGain();
            gainNode.connect(this.masterGain);

            // Busy tone: 480Hz + 620Hz
            const osc1 = this.audioContext.createOscillator();
            const osc2 = this.audioContext.createOscillator();

            osc1.frequency.value = 480;
            osc2.frequency.value = 620;
            osc1.type = 'sine';
            osc2.type = 'sine';

            const gain1 = this.audioContext.createGain();
            const gain2 = this.audioContext.createGain();
            gain1.gain.value = 0.12;
            gain2.gain.value = 0.12;

            osc1.connect(gain1);
            osc2.connect(gain2);
            gain1.connect(gainNode);
            gain2.connect(gainNode);

            osc1.start(time);
            osc2.start(time);

            gainNode.gain.setValueAtTime(1, time);
            gainNode.gain.setValueAtTime(1, time + duration - 0.02);
            gainNode.gain.linearRampToValueAtTime(0, time + duration);

            osc1.stop(time + duration + 0.01);
            osc2.stop(time + duration + 0.01);

            pulseCount++;

            // 0.25s on, 0.25s off pattern
            this.currentSound = setTimeout(() => playPulse(), 500);
        };

        playPulse();
    }

    /**
     * Stop all sounds
     */
    stop() {
        this.isPlaying = false;
        this.soundType = null;

        if (this.currentSound) {
            clearTimeout(this.currentSound);
            this.currentSound = null;
        }

        // Stop any active oscillators
        this.oscillators.forEach(osc => {
            try {
                osc.stop();
            } catch (e) {
                // Oscillator may already be stopped
            }
        });
        this.oscillators = [];
    }

    /**
     * Set master volume (0 to 1)
     */
    setVolume(volume) {
        if (this.masterGain) {
            this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
        }
    }

    /**
     * Clean up resources
     */
    cleanup() {
        this.stop();
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = null;
            this.masterGain = null;
        }
    }
}

// Export singleton instance
export const callSounds = new CallSoundsService();
export default CallSoundsService;
